import { Mail, Phone, MapPin, Send, MessageCircle, Calendar, Clock, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import emailjs from '@emailjs/browser';
import { SEOHead } from "@/components/SEO";
import { LocalBusinessSchema } from "@/components/Schema";

const Contact = () => {
  const [searchParams] = useSearchParams();
  const projectParam = searchParams.get('project');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    budget: '',
    timeline: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Map project slugs to readable titles
  const projectTitles: Record<string, string> = {
    'ai-agents': 'AI Agents Platform',
    'executive-ai': 'Executive AI Dashboard',
    'ai-workflow': 'AI Workflow Engine',
    'ai-ecommerce': 'AI-Powered E-commerce',
    'netsuite-ai': 'NetSuite AI Scripts',
    'powerbi-analytics': 'Real-time Analytics Dashboard'
  };

  useEffect(() => {
    if (projectParam && projectTitles[projectParam]) {
      const projectTitle = projectTitles[projectParam];
      setFormData(prev => ({
        ...prev,
        message: `I'm interested in learning more about the ${projectTitle} solution...`
      }));
    }
  }, [projectParam]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // EmailJS configuration from environment variables
      const SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID || 'service_fortifytek';
      const TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID || 'template_contact';
      const PUBLIC_KEY = import.meta.env.VITE_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY';
      
      // Check if EmailJS is configured
      if (PUBLIC_KEY === 'YOUR_PUBLIC_KEY') {
        console.warn('EmailJS not configured. Form submission simulated.');
        // Simulate submission if EmailJS is not configured
        await new Promise(resolve => setTimeout(resolve, 2000));
      } else {
        // Initialize EmailJS and send email
        emailjs.init(PUBLIC_KEY);
        
        // Send email
        const templateParams = {
          from_name: formData.name,
          from_email: formData.email,
          company: formData.company || 'Not provided',
          phone: formData.phone || 'Not provided',
          service: formData.service,
          budget: formData.budget || 'Not specified',
          timeline: formData.timeline || 'Not specified',
          message: formData.message,
          to_email: 'ftsupport@fortifytek.com',
        };
        
        await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams);
      }
      
      setIsSubmitting(false);
      setIsSubmitted(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ 
          name: '', email: '', company: '', phone: '', 
          service: '', budget: '', timeline: '', message: '' 
        });
      }, 3000);
    } catch (error) {
      console.error('Failed to send message:', error);
      setIsSubmitting(false);
      alert('Failed to send message. Please try again or contact us directly at ftsupport@fortifytek.com');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const services = [
    "AI Solutions & Machine Learning",
    "NetSuite ERP Development", 
    "Shopify Plus Development",
    "Celigo Integration Platform",
    "PowerBI & Analytics",
    "Custom Application Development",
    "Other / Multiple Services"
  ];

  const budgetRanges = [
    "Under $10,000",
    "$10,000 - $25,000", 
    "$25,000 - $50,000",
    "$50,000 - $100,000",
    "Over $100,000",
    "Let's Discuss"
  ];

  const timelines = [
    "ASAP (Rush Project)",
    "1-2 months",
    "3-6 months", 
    "6+ months",
    "Flexible"
  ];

  return (
    <>
      <SEOHead
        title="Contact FortifyTek | Get a Free Consultation"
        description="Contact FortifyTek for a free consultation on AI solutions, NetSuite ERP, Shopify Plus, and enterprise technology services. We reply within 24 hours."
        keywords={[
          'Contact FortifyTek',
          'Technology Consultation',
          'Hire AI Developers',
          'NetSuite Support',
          'Shopify Plus Experts',
          'Enterprise Tech Support'
        ]}
        canonicalUrl="https://www.fortifytek.com/contact"
        schemaType="WebPage"
      />
      <LocalBusinessSchema />
      
      <div className="min-h-screen bg-background text-foreground">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-40 pb-20 relative overflow-hidden bg-background">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-8 leading-tight">
              <span className="block mb-2">
                Let's Build Something
              </span>
              <span className="block text-amber-400 pb-2">
                Extraordinary Together
              </span>
            </h1>
            
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
              Ready to transform your business? Get in touch with our expert team today.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Contact Form */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-white mb-2">
                {projectParam && projectTitles[projectParam] ? `Inquire about ${projectTitles[projectParam]}` : 'Start Your Project'}
              </h2>
              <p className="text-muted-foreground mb-8">Tell us about your vision and we'll bring it to life</p>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">Full Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                      className="bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-amber-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">Work Email *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      required
                      className="bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-amber-400"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">Company</label>
                    <Input
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company"
                      className="bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-amber-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">Phone</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      className="bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-amber-400"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Service Needed *</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full bg-zinc-950 border border-zinc-800 text-white focus:border-amber-400 rounded-md px-3 py-2"
                  >
                    <option value="">Select a service</option>
                    {services.map((service, index) => (
                      <option key={index} value={service} className="bg-zinc-900 text-white">
                        {service}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">Budget Range</label>
                    <select
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      className="w-full bg-zinc-950 border border-zinc-800 text-white focus:border-amber-400 rounded-md px-3 py-2"
                    >
                      <option value="">Select budget</option>
                      {budgetRanges.map((range, index) => (
                        <option key={index} value={range} className="bg-zinc-900 text-white">
                          {range}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">Timeline</label>
                    <select
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleChange}
                      className="w-full bg-zinc-950 border border-zinc-800 text-white focus:border-amber-400 rounded-md px-3 py-2"
                    >
                      <option value="">Select timeline</option>
                      {timelines.map((timeline, index) => (
                        <option key={index} value={timeline} className="bg-zinc-900 text-white">
                          {timeline}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Project Details *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project requirements, goals, and any specific challenges you're facing..."
                    rows={6}
                    required
                    className="bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600 resize-none focus:border-amber-400"
                  />
                </div>
                
                <Button 
                  type="submit"
                  size="lg"
                  disabled={isSubmitting || isSubmitted}
                  className="w-full bg-white text-black hover:bg-zinc-200 disabled:bg-zinc-700 disabled:text-zinc-400 py-4 rounded-xl text-lg font-medium transition-colors group"
                >
                  {isSubmitting ? "Sending..." : isSubmitted ? "Message Sent!" : "Send Message"}
                  <Send className={`ml-2 w-5 h-5 transition-transform ${isSubmitting ? 'animate-spin' : 'group-hover:translate-x-1'}`} />
                </Button>
                
                <p className="text-sm text-zinc-500 text-center">
                  We typically respond within 2 hours during business days
                </p>
              </form>
            </div>

            {/* Contact Info & Quick Actions */}
            <div className="space-y-8">
              
              {/* Contact Information */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6">Get In Touch</h3>
                
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-12 h-12 bg-blue-600/10 rounded-xl">
                      <Mail className="w-6 h-6 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Email</p>
                      <p className="text-muted-foreground">ftsupport@fortifytek.com</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-12 h-12 bg-amber-500/10 rounded-xl">
                      <Phone className="w-6 h-6 text-amber-500" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Phone</p>
                      <p className="text-muted-foreground">+1 (424) 242-4140</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-12 h-12 bg-purple-500/10 rounded-xl">
                      <MapPin className="w-6 h-6 text-purple-500" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Location</p>
                      <p className="text-muted-foreground">Riverside, CA, USA</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Contact Options */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Button
                  variant="outline"
                  className="bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800 hover:border-zinc-700 p-6 h-auto flex-col gap-2"
                  onClick={() => window.open('tel:+14242424140', '_self')}
                >
                  <Phone className="w-6 h-6 text-blue-500" />
                  <span>Call Now</span>
                  <span className="text-xs text-muted-foreground">Immediate response</span>
                </Button>
                <Button
                  variant="outline"
                  className="bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800 hover:border-zinc-700 p-6 h-auto flex-col gap-2"
                  onClick={() => window.open('https://calendly.com/fortifytek', '_blank')}
                >
                  <Calendar className="w-6 h-6 text-amber-500" />
                  <span>Book Meeting</span>
                  <span className="text-xs text-muted-foreground">Schedule consultation</span>
                </Button>
              </div>

              {/* Why Choose Us */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-white mb-4">Why Work With FortifyTek?</h3>
                <ul className="space-y-4 text-zinc-400">
                  <li className="flex items-center gap-3">
                    <Award className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <span>Effective solutions focused on results</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span>Efficient delivery with fast turnaround</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <MessageCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>24/7 expert support and consultation</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-purple-500 rounded-full flex-shrink-0"></div>
                    <span>Transparent pricing with no hidden costs</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      </div>
    </>
  );
};

export default Contact;